// src/components/ctb/ExportacionCtb/ExportacionCtbContainer.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

// Componente presentacional
import ExportacionCtbPresentation from './ExportacionCtbPresentation';

// Hooks personalizados
import { useApi, useForm, useNotification } from '../../../hooks';

// Servicios y utilidades
import { ctbService } from '../../../services/ctbService';
import { formatearMonto, formatearFecha } from '../../../utils/formatters';
import config from '../../../config.js';

const ExportacionCtbContainer = () => {
  const { empresaActiva } = useSelector(state => state.empresa);
  const { showNotification } = useNotification();
  
  // Estado para exportación y carga
  const [soloPendientes, setSoloPendientes] = useState(true);
  const [openParametros, setOpenParametros] = useState(false);
  
  // API para periodos
  const [periodos, setPeriodos] = useState([]);
  
  // Form para parámetros
  const parametrosForm = useForm({
    folio_inicial: '',
    cuenta_iva: '',
    cuenta_otros_impuestos: '',
    cuenta_proveedores: ''
  });
  
  // API hooks
  const { 
    data: exportaciones, 
    loading: loadingExportaciones, 
    error: errorExportaciones, 
    execute: cargarExportaciones 
  } = useApi(ctbService.getExportaciones);
  
  const { 
    loading: loadingParametros, 
    execute: cargarParametros 
  } = useApi(ctbService.getParametros);
  
  const { 
    loading: guardandoParametros, 
    execute: guardarParametro 
  } = useApi(ctbService.guardarParametro);
  
  const { 
    loading: exportando, 
    error: errorExportacion, 
    execute: exportarAsientos 
  } = useApi(ctbService.exportarAsientos);
  
  // Form principal
  const exportacionForm = useForm({
    rutEmpresa: empresaActiva?.rut || '',
    dvEmpresa: empresaActiva?.dv || '',
    periodo: '',
    soloPendientes: true
  });
  
  // Efecto inicial para cargar periodos
  useEffect(() => {
    // Importar la función de generación de períodos
    import('../../../utils/periodos').then(({ generarPeriodos }) => {
      const opciones = generarPeriodos();
      setPeriodos(opciones);
      if (opciones.length > 0) {
        exportacionForm.setValues(prev => ({ ...prev, periodo: opciones[0].value }));
      }
    });
  }, []);
  
  // Cargar exportaciones cuando cambia el periodo
  useEffect(() => {
    const cargar = async () => {
      if (empresaActiva?.rut && exportacionForm.values.periodo) {
        await cargarExportaciones(empresaActiva.rut, exportacionForm.values.periodo);
      }
    };
    cargar();
  }, [empresaActiva, exportacionForm.values.periodo, cargarExportaciones]);
  
  // Actualizar empresa activa en el form
  useEffect(() => {
    if (empresaActiva) {
      exportacionForm.setValues(prev => ({
        ...prev,
        rutEmpresa: empresaActiva.rut,
        dvEmpresa: empresaActiva.dv
      }));
    }
  }, [empresaActiva]);
  
  // Cargar parámetros
  const handleCargarParametros = useCallback(async () => {
    if (!empresaActiva?.rut) return;
    
    try {
      const data = await cargarParametros(empresaActiva.rut);
      
      // Convertir array a objeto
      const paramObj = {};
      data.forEach(param => {
        paramObj[param.codigo] = param.valor;
      });
      
      // Inicializar formulario
      parametrosForm.setValues({
        folio_inicial: paramObj.folio_inicial || '',
        cuenta_iva: paramObj.cuenta_iva || '',
        cuenta_otros_impuestos: paramObj.cuenta_otros_impuestos || '',
        cuenta_proveedores: paramObj.cuenta_proveedores || ''
      });
    } catch (errorObj) {
      console.error("Error al cargar parámetros:", errorObj);
      showNotification('Error al cargar parámetros contables', 'error');
    }
  }, [empresaActiva, cargarParametros, showNotification]);
  
  // Guardar parámetros
  const handleGuardarParametros = async () => {
    if (!empresaActiva?.rut) return;
    
    try {
      // Guardar cada parámetro
      const promesas = Object.entries(parametrosForm.values).map(([codigo, valor]) => {
        return guardarParametro({
          rut_empresa: empresaActiva.rut,
          codigo,
          valor,
          descripcion: getDescripcionParametro(codigo)
        });
      });
      
      await Promise.all(promesas);
      
      // Recargar parámetros
      await handleCargarParametros();
      
      // Cerrar diálogo
      setOpenParametros(false);
      showNotification('Parámetros contables guardados correctamente', 'success');
    } catch (errorObj) {
      console.error("Error al guardar parámetros:", errorObj);
      showNotification('Error al guardar parámetros contables', 'error');
    }
  };
  
  // Obtener descripción de parámetro
  const getDescripcionParametro = (codigo) => {
    const descripciones = {
      folio_inicial: "Folio inicial para la generación de asientos",
      cuenta_iva: "Cuenta contable para IVA",
      cuenta_otros_impuestos: "Cuenta contable para otros impuestos",
      cuenta_proveedores: "Cuenta contable para proveedores"
    };
    
    return descripciones[codigo] || "";
  };
  
  // Exportar asientos contables
  const handleExportarAsientos = async () => {
    if (!empresaActiva?.rut || !exportacionForm.values.periodo) {
      showNotification('Debe seleccionar un período', 'error');
      return;
    }
    
    try {
      const resultado = await exportarAsientos({
        rut_empresa: empresaActiva.rut,
        dv_empresa: empresaActiva.dv,
        periodo: exportacionForm.values.periodo,
        separador: ";",
        solo_pendientes: soloPendientes
      });
      
      showNotification(`Exportación completada. Se generaron ${resultado.cantidad_asientos} asientos para ${resultado.cantidad_movimientos} movimientos.`, 'success');
      
      // Recargar exportaciones
      cargarExportaciones(empresaActiva.rut, exportacionForm.values.periodo);
    } catch (errorObj) {
      console.error("Error al exportar asientos:", errorObj);
      // El error ya es manejado por useApi
    }
  };
  
  // Descargar archivo
  const handleDescargarArchivo = (exportacionId) => {
    // Crear la URL completa del backend
    const apiUrl = `${config.API_BASE_URL || 'http://localhost:8101/api/v1'}/ctb/exportaciones/${exportacionId}/descargar`;
    
    // Obtener el token JWT
    const jwtToken = localStorage.getItem('token');
    
    // Hacer la solicitud para la descarga
    fetch(apiUrl, {
      headers: {
        'Authorization': `Bearer ${jwtToken}`
      }
    })
    .then(response => response.blob())
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `asientos_${exportacionId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    })
    .catch(error => {
      console.error("Error descargando el archivo:", error);
      showNotification("Error al descargar el archivo", "error");
    });
  };

  // Handlers para FilterBar
  const handleFiltrar = useCallback(() => {
    if (empresaActiva?.rut && exportacionForm.values.periodo) {
      cargarExportaciones(empresaActiva.rut, exportacionForm.values.periodo);
    }
  }, [empresaActiva, exportacionForm.values.periodo, cargarExportaciones]);
  
  const handleLimpiarFiltros = useCallback(() => {
    // Reiniciar filtros si es necesario
    exportacionForm.setValues(prev => ({
      ...prev,
      periodo: periodos.length > 0 ? periodos[0].value : ''
    }));
  }, [periodos]);
  
  const handleChangeSoloPendientes = useCallback((e) => {
    setSoloPendientes(e.target.checked);
  }, []);
  
  const handleOpenParametros = useCallback(() => {
    handleCargarParametros();
    setOpenParametros(true);
  }, [handleCargarParametros]);
  
  const handleCloseParametros = useCallback(() => {
    setOpenParametros(false);
  }, []);
  
  // Columnas para la tabla de exportaciones
  const exportacionesColumns = [
    { 
      field: 'fecha_exportacion', 
      headerName: 'Fecha', 
      renderCell: val => formatearFecha(val) 
    },
    { field: 'periodo', headerName: 'Período' },
    { 
      field: 'folios', 
      headerName: 'Folios',
      renderCell: (_, row) => `${row.folio_inicial} - ${row.folio_final}`
    },
    { 
      field: 'cantidad_movimientos', 
      headerName: 'Movimientos',
      align: 'center'
    },
    { 
      field: 'cantidad_asientos', 
      headerName: 'Asientos',
      align: 'center'
    },
    { 
      field: 'valor_total', 
      headerName: 'Valor Total',
      align: 'right',
      renderCell: val => formatearMonto(val)
    },
    {
      field: 'estado',
      headerName: 'Estado',
      align: 'center',
      renderCell: val => val // Renderizado delegado al componente presentacional
    }
  ];
  
  // Acciones para la tabla
  const exportacionesActions = [
    {
      icon: 'download', // Simplemente un identificador, el componente real está en el presentacional
      tooltip: 'Descargar archivo CSV',
      onClick: handleDescargarArchivo,
      disabled: (row) => !row.archivo_generado
    }
  ];
  
  // Pasar todas las props al componente presentacional
  return (
    <ExportacionCtbPresentation
      // Datos empresa
      empresaActiva={empresaActiva}
      
      // Estados UI
      soloPendientes={soloPendientes}
      openParametros={openParametros}
      
      // Datos
      periodos={periodos.map(p => ({ value: p.value, label: p.label }))}
      exportaciones={exportaciones || []}
      
      // Estados de carga
      loadingExportaciones={loadingExportaciones}
      errorExportaciones={errorExportaciones}
      loadingParametros={loadingParametros}
      guardandoParametros={guardandoParametros}
      exportando={exportando}
      errorExportacion={errorExportacion}
      
      // Formularios
      exportacionForm={exportacionForm}
      parametrosForm={parametrosForm}
      
      // Handlers
      onChangeSoloPendientes={handleChangeSoloPendientes}
      onOpenParametros={handleOpenParametros}
      onCloseParametros={handleCloseParametros}
      onFiltrar={handleFiltrar}
      onLimpiarFiltros={handleLimpiarFiltros}
      onExportarAsientos={handleExportarAsientos}
      onGuardarParametros={handleGuardarParametros}
      onDescargarArchivo={handleDescargarArchivo}
      
      // Config tabla
      exportacionesColumns={exportacionesColumns}
      exportacionesActions={exportacionesActions}
    />
  );
};

export default ExportacionCtbContainer;